import { useEffect, useCallback, useState, type FC } from 'react'
import { useRouter } from 'next/router'

import { Badge } from '@wartek-id/badge'

import { Header } from 'app/KinerjaShared/components/Header'
import { InternalServerError } from 'utils/customError'
import { usePelaksanaanPraktikKinerja } from 'app/KinerjaPenilikPegawaiPage/hooks/usePelaksanaanPraktikKinerja'
import { PENGELOLAAN_KINERJA_PENILIK_PEGAWAI_PATH } from 'app/KinerjaShared/constants/path'
import usePenilikPeriodGuard from 'app/KinerjaPenilikPegawaiPage/hooks/usePenilikPeriodGuard'
import CardForm from 'app/KinerjaPenilikPegawaiPage/pages/Pelaksanaan/PraktikKinerja/PendampinganSatuanPendidikan/components/CardForm'
import OnceLoaded from 'app/KinerjaPenilikPegawaiPage/pages/Pelaksanaan/PraktikKinerja/components/OnceLoaded'
import CardFormPreview from 'app/KinerjaPenilikPegawaiPage/pages/Pelaksanaan/PraktikKinerja/PendampinganSatuanPendidikan/components/CardFormPreview'
import { usePenilikTrackEvent } from 'app/KinerjaPenilikPegawaiPage/hooks/usePenilikTrackEvent'
import LoadingSpinner from '../components/LoadingSpinner'

import type {
  PraktikKinerjaFormName,
  PraktikKinerjaPendampinganFormValue,
} from 'types/kinerja-penilik-pegawai'

type PendampinganSatuanPendidikanProps = {}

const formName: PraktikKinerjaFormName = 'pendampingan_sekolah'

const PendampinganSatuanPendidikan: FC<
  PendampinganSatuanPendidikanProps
> = () => {
  const router = useRouter()
  const { trackEvent } = usePenilikTrackEvent()
  const { isPeriodSuccess, pelaksanaanGuard } = usePenilikPeriodGuard()

  const [isPageLoading, setPageLoading] = useState(true)

  // Entry Point Guards

  // Prepare form values from the API
  const formValues =
    usePelaksanaanPraktikKinerja<PraktikKinerjaPendampinganFormValue>({
      formName,
    })

  const isSuccess = !formValues.isLoading && formValues.isSuccess

  const goBack = useCallback(() => {
    if (window.history.state.idx <= 1)
      router.push(
        `${PENGELOLAAN_KINERJA_PENILIK_PEGAWAI_PATH}/pelaksanaan/praktik-kinerja`
      )
    else router.back()
  }, [router])

  useEffect(() => {
    if (
      formValues.penilikSummaryHookResult.summaryFetchStatus === 'success' &&
      !formValues.isLoading &&
      !formValues.isSuccess
    ) {
      throw new InternalServerError(
        'API_PATH',
        new Error('Failed to fetch summary or form values')
      )
    }

    if (formValues.isSuccess) {
      if (!formValues.penilikSummaryHookResult.isPelaksanaanState) {
        router.replace(PENGELOLAAN_KINERJA_PENILIK_PEGAWAI_PATH)
      } else if (formValues.isLocked) {
        goBack()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formValues.isLoading,
    formValues.isLocked,
    formValues.isSuccess,
    formValues.penilikSummaryHookResult.isPelaksanaanState,
    formValues.penilikSummaryHookResult.summaryFetchStatus,
  ])

  useEffect(() => {
    if (isPeriodSuccess && !pelaksanaanGuard.isOpen) {
      router.replace(PENGELOLAAN_KINERJA_PENILIK_PEGAWAI_PATH)
    } else if (isPeriodSuccess && formValues.isSuccess) {
      setPageLoading(false)
    }
  }, [isPeriodSuccess, pelaksanaanGuard.isOpen, router, formValues.isSuccess])

  if (isPageLoading) {
    return (
      <div className="px-3 mx-auto max-w-7xl py-9">
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <>
      <div className="px-3 mx-auto max-w-7xl py-9 space-y-7">
        <Header
          title={
            <div className="flex items-center gap-x-3">
              Pendampingan Satuan Pendidikan
              {formValues.isViewOnly && !formValues.isAppeal && (
                <Badge variant="success">Selesai</Badge>
              )}
              {formValues.isAppeal && (
                <Badge variant="neutral">Perubahan sedang diajukan</Badge>
              )}
            </div>
          }
        />

        <OnceLoaded loaded={isSuccess}>
          {!formValues.isViewOnly ? (
            <CardForm
              formValues={formValues}
              onSubmitSuccess={() => {
                trackEvent('pelaksanaan pegawai', 'submit form B')
                goBack()
              }}
            />
          ) : (
            <CardFormPreview
              formValues={formValues}
              formName={formName}
              showAppealButton
            />
          )}
        </OnceLoaded>
      </div>
    </>
  )
}

export default PendampinganSatuanPendidikan
